/**
 * Role configuration with display labels and colors
 * Single source of truth for role display across the application
 */

// Every role gets a distinct hue. Manager vs staff of the same department
// intentionally use different hues (not just shades) so they are easy to
// tell apart in chips, menus, and the admin user list.
export const ROLE_CONFIG = {
  admin: {
    label: 'Admin',
    color: '#DC2626', // red
    bg: '#FEE2E2',
  },
  director: {
    label: 'Director',
    color: '#7C3AED', // violet
    bg: '#EDE9FE',
  },
  project_manager: {
    label: 'Project Manager',
    color: '#2563EB', // blue
    bg: '#DBEAFE',
  },
  site_manager: {
    label: 'Site Manager',
    color: '#16A34A', // green
    bg: '#DCFCE7',
  },
  site_engineer: {
    label: 'Site Engineer',
    color: '#0891B2', // cyan
    bg: '#CFFAFE',
  },
  commercial_manager: {
    label: 'Commercial Manager',
    color: '#EA580C', // orange
    bg: '#FFEDD5',
  },
  commercial_staff: {
    label: 'Commercial Staff',
    color: '#CA8A04', // gold
    bg: '#FEF9C3',
  },
  customer: {
    label: 'Customer',
    color: '#DB2777', // pink
    bg: '#FCE7F3',
  },
  stakeholder: {
    label: 'Stakeholder',
    color: '#9D174D', // dark pink
    bg: '#FCE7F3',
  },
  staff: {
    label: 'Staff',
    color: '#475569', // slate
    bg: '#F1F5F9',
  },
  procurement_ho_manager: {
    label: 'Procurement SPV',
    color: '#9333EA', // purple
    bg: '#F3E8FF',
  },
  procurement_ho_staff: {
    label: 'Procurement Staff',
    color: '#C026D3', // fuchsia
    bg: '#FAE8FF',
  },
  operational_manager: {
    label: 'Operational Manager',
    color: '#4338CA', // indigo
    bg: '#E0E7FF',
  },
  operational_staff: {
    label: 'Operational Staff',
    color: '#0284C7', // sky
    bg: '#E0F2FE',
  },
  finance_manager: {
    label: 'Finance Manager',
    color: '#0D9488', // teal
    bg: '#CCFBF1',
  },
  finance_staff: {
    label: 'Finance Staff',
    color: '#65A30D', // lime
    bg: '#ECFCCB',
  },
  accounting: {
    label: 'Accounting / Releaser',
    color: '#B45309', // brown
    bg: '#FEF3C7',
  },
  gs: {
    label: 'General Support (GS)',
    color: '#BE123C', // rose
    bg: '#FFE4E6',
  },
};

/**
 * Get role label with proper formatting
 * @param {string} role - The role name (e.g., 'commercial_manager')
 * @returns {string} Formatted label (e.g., 'Commercial Manager')
 */
export const getRoleLabel = (role) => {
  if (!role) return 'User';
  
  const normalizedRole = role.toLowerCase().trim();
  return ROLE_CONFIG[normalizedRole]?.label || 
    role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get role color
 * @param {string} role - The role name
 * @returns {string} Color hex code
 */
export const getRoleColor = (role) => {
  if (!role) return '#64748B';
  
  const normalizedRole = role.toLowerCase().trim();
  return ROLE_CONFIG[normalizedRole]?.color || '#64748B';
};

/**
 * Get role background color
 * @param {string} role - The role name
 * @returns {string} Background color hex code
 */
export const getRoleBgColor = (role) => {
  if (!role) return '#F1F5F9';
  
  const normalizedRole = role.toLowerCase().trim();
  return ROLE_CONFIG[normalizedRole]?.bg || '#F1F5F9';
};

/**
 * Get complete role configuration
 * @param {string} role - The role name
 * @returns {object} Role config with label, color, bg
 */
export const getRoleConfig = (role) => {
  if (!role) {
    return {
      label: 'User',
      color: '#64748B',
      bg: '#F1F5F9',
    };
  }
  
  const normalizedRole = role.toLowerCase().trim();
  return ROLE_CONFIG[normalizedRole] || {
    label: role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
    color: '#64748B',
    bg: '#F1F5F9',
  };
};
